<template>
	<div class="collapsible-article" ref="articleContainer">
		<div v-html="processedContent" @click="handleClick"></div>
	</div>
</template>

<script setup>
import { createApp, computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ArticleEmbeddedMedia from '@/components/common/ArticleEmbeddedMedia.vue'
import {
	isBitrixLaunchableMediaUrl,
	isDirectVideoUrl,
	normalizeMediaUrl,
} from '@/utils/bitrixVideoSlider'

const props = defineProps({
	content: {
		type: String,
		default: '',
	},
	collapsibleHeadings: {
		type: Array,
		default: () => ['h1', 'h2', 'h3'],
	},
	initiallyExpanded: {
		type: Boolean,
		default: true,
	},
	enableBitrixVideoSlider: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['open-media'])

const articleContainer = ref(null)
const expandedSections = ref(new Set())
const mountedMediaApps = []

let headingCounter = 0

const extractYouTubeId = src =>
	(src.match(/youtube(?:-nocookie)?\.com\/embed\/([^?&/]+)/i) || [])[1] ||
	(src.match(/[?&]v=([^&]+)/i) || [])[1] ||
	(src.match(/youtu\.be\/([^?&/]+)/i) || [])[1] ||
	''

const extractVimeoId = src =>
	(src.match(/player\.vimeo\.com\/video\/([^?&/]+)/i) || [])[1] ||
	(src.match(/vimeo\.com\/(?:video\/)?([^?&/]+)/i) || [])[1] ||
	''

const isVideoFileUrl = src => {
	const normalized = normalizeMediaUrl(src)
	return isDirectVideoUrl(normalized)
}

const parseLocalVideoUrl = rawUrl => {
	const value = String(rawUrl || '').trim()
	if (!value) return { src: '', poster: '' }

	try {
		const parsed = new URL(value, 'https://local.invalid')
		const poster = parsed.searchParams.get('poster') || ''
		parsed.searchParams.delete('poster')

		return {
			src: normalizeMediaUrl(`${parsed.pathname}${parsed.search}`),
			poster: normalizeMediaUrl(poster),
		}
	} catch {
		return {
			src: normalizeMediaUrl(value),
			poster: '',
		}
	}
}

const resolveMediaProvider = src => {
	const value = String(src || '').trim()
	if (!value) return ''
	if (extractYouTubeId(value)) return 'youtube'
	if (extractVimeoId(value)) return 'vimeo'
	if (isVideoFileUrl(value)) return 'video'
	return 'iframe'
}

const buildMediaPlaceholderMarkup = payload => {
	const serialized = encodeURIComponent(
		JSON.stringify({
			provider: payload?.provider || 'iframe',
			src: payload?.src || '',
			title: payload?.title || 'Видео',
			poster: payload?.poster || '',
		}),
	)

	return `<div class="rte-media-placeholder" data-rte-media-placeholder="true" data-rte-media-props="${serialized}"></div>`
}

const transformLegacyEmbeddedCards = doc => {
	const figures = doc.querySelectorAll('figure.rte-video-card')

	figures.forEach(figure => {
		const video = figure.querySelector('video')
		const iframe = figure.querySelector('iframe')
		const link = figure.querySelector('a[data-rte-video-provider], a[href]')
		const title =
			figure.querySelector('.rte-video-card__title')?.textContent?.trim() ||
			figure.querySelector('figcaption')?.textContent?.trim() ||
			'Видео'

		if (video) {
			const rawSrc = video.getAttribute('src') || ''
			if (!rawSrc) return

			const poster = video.getAttribute('poster') || ''
			const { src, poster: parsedPoster } = parseLocalVideoUrl(rawSrc)
			const mediaSrc = src || normalizeMediaUrl(rawSrc)
			const mediaPoster = parsedPoster || normalizeMediaUrl(poster)

			figure.outerHTML = buildMediaPlaceholderMarkup({
				provider: 'video',
				src: mediaSrc,
				title,
				poster: mediaPoster,
			})
			return
		}

		let mediaSrc = ''
		if (iframe) {
			mediaSrc = iframe.getAttribute('src') || ''
		} else if (link) {
			mediaSrc = link.getAttribute('href') || ''
		}

		if (!mediaSrc) return

		figure.outerHTML = buildMediaPlaceholderMarkup({
			provider: resolveMediaProvider(mediaSrc),
			src: normalizeMediaUrl(mediaSrc),
			title,
		})
	})
}

const transformOembedMediaFigures = doc => {
	const figures = doc.querySelectorAll('figure.media')

	figures.forEach(figure => {
		const oembed = figure.querySelector('oembed[url]')
		if (!oembed) return

		const rawUrl = oembed.getAttribute('url') || ''
		if (!rawUrl) return

		const title = figure.querySelector('figcaption')?.textContent?.trim() || 'Видео'
		const provider = resolveMediaProvider(rawUrl)

		if (provider === 'video') {
			const { src, poster } = parseLocalVideoUrl(rawUrl)
			figure.innerHTML = buildMediaPlaceholderMarkup({
				provider: 'video',
				src,
				title,
				poster,
			})
			return
		}

		figure.innerHTML = buildMediaPlaceholderMarkup({
			provider,
			src: normalizeMediaUrl(rawUrl),
			title,
		})
	})
}

const processedContent = computed(() => {
	if (!props.content) return ''

	const parser = new DOMParser()
	const doc = parser.parseFromString(props.content, 'text/html')

	transformOembedMediaFigures(doc)
	transformLegacyEmbeddedCards(doc)

	headingCounter = 0

	const headingsSelector = props.collapsibleHeadings.join(', ')
	const headingElements = doc.querySelectorAll(headingsSelector)

	headingElements.forEach(heading => {
		const headingId = `collapsible-heading-${headingCounter++}`
		const headingStyle = heading.getAttribute('style') || ''

		heading.setAttribute('data-collapsible-id', headingId)
		heading.classList.add('collapsible-heading')

		if (headingStyle) {
			heading.setAttribute('data-original-style', headingStyle)
		}

		const icon = doc.createElement('span')
		icon.className = 'collapsible-icon'
		icon.innerHTML = '▼'
		heading.insertBefore(icon, heading.firstChild)

		if (props.initiallyExpanded) {
			expandedSections.value.add(headingId)
		}

		const wrapper = doc.createElement('div')
		wrapper.className = 'collapsible-content'
		wrapper.setAttribute('data-for-heading', headingId)

		const headingLevel = parseInt(heading.tagName.charAt(1), 10)
		let sibling = heading.nextElementSibling
		const elementsToWrap = []

		while (sibling) {
			const siblingTag = sibling.tagName.toLowerCase()
			if (props.collapsibleHeadings.includes(siblingTag)) {
				const siblingLevel = parseInt(siblingTag.charAt(1), 10)
				if (siblingLevel <= headingLevel) {
					break
				}
			}

			elementsToWrap.push(sibling)
			sibling = sibling.nextElementSibling
		}

		elementsToWrap.forEach(element => {
			wrapper.appendChild(element)
		})

		if (heading.parentNode) {
			heading.parentNode.insertBefore(wrapper, heading.nextSibling)
		}
	})

	return doc.body.innerHTML
})

const cleanupMountedMediaApps = () => {
	while (mountedMediaApps.length) {
		const app = mountedMediaApps.pop()
		app?.unmount()
	}
}

const mountEmbeddedMedia = () => {
	if (!articleContainer.value) return

	cleanupMountedMediaApps()

	const placeholders = articleContainer.value.querySelectorAll(
		'[data-rte-media-placeholder="true"]',
	)

	placeholders.forEach(placeholder => {
		const raw = placeholder.getAttribute('data-rte-media-props') || ''
		if (!raw) return

		try {
			const payload = JSON.parse(decodeURIComponent(raw))
			const app = createApp(ArticleEmbeddedMedia, {
				...payload,
				enableBitrixVideoSlider: props.enableBitrixVideoSlider,
				onOpenMedia: mediaPayload => emit('open-media', mediaPayload),
			})

			app.mount(placeholder)
			mountedMediaApps.push(app)
		} catch (error) {
			console.warn('Не удалось смонтировать embedded media:', error)
		}
	})
}

const updateVisibility = () => {
	if (!articleContainer.value) return

	const headings = articleContainer.value.querySelectorAll('.collapsible-heading')

	headings.forEach(heading => {
		const headingId = heading.getAttribute('data-collapsible-id')
		const content = articleContainer.value.querySelector(`[data-for-heading="${headingId}"]`)
		const icon = heading.querySelector('.collapsible-icon')

		if (!content) return

		const isExpanded = expandedSections.value.has(headingId)

		if (isExpanded) {
			content.classList.remove('collapsed')
			heading.classList.remove('collapsed')
			if (icon) icon.innerHTML = '▼'
			return
		}

		content.classList.add('collapsed')
		heading.classList.add('collapsed')
		if (icon) icon.innerHTML = '▶'
	})
}

const syncRenderedContent = async () => {
	await nextTick()
	mountEmbeddedMedia()
	updateVisibility()
}

const handleClick = event => {
	if (props.enableBitrixVideoSlider) {
		const link = event.target.closest('a[href]')
		if (link) {
			const href = link.getAttribute('href') || ''
			if (isBitrixLaunchableMediaUrl(href)) {
				event.preventDefault()
				event.stopPropagation()
				emit('open-media', {
					url: href,
					title: link.textContent?.trim() || 'Видео',
				})
				return
			}
		}
	}

	const target = event.target.closest('.collapsible-heading')
	if (!target) return

	const headingId = target.getAttribute('data-collapsible-id')
	if (!headingId) return

	if (expandedSections.value.has(headingId)) {
		expandedSections.value.delete(headingId)
	} else {
		expandedSections.value.add(headingId)
	}

	updateVisibility()
}

const collapseAll = () => {
	expandedSections.value.clear()
	updateVisibility()
}

const expandAll = () => {
	const headings = articleContainer.value?.querySelectorAll('.collapsible-heading') || []
	headings.forEach(heading => {
		const id = heading.getAttribute('data-collapsible-id')
		if (id) expandedSections.value.add(id)
	})
	updateVisibility()
}

onMounted(syncRenderedContent)

watch(
	() => [props.content, props.enableBitrixVideoSlider],
	() => {
		syncRenderedContent()
	},
)

onBeforeUnmount(() => {
	cleanupMountedMediaApps()
})

defineExpose({
	collapseAll,
	expandAll,
})
</script>

<style scoped>
.collapsible-article {
	line-height: 1.8;
	color: #1f2937;
}

.collapsible-article :deep(.collapsible-heading) {
	cursor: pointer;
	user-select: none;
	position: relative;
	padding: 8px 0 8px 24px;
	margin: 16px 0 8px 0;
	background: transparent;
	border-radius: 0;
	border-left: 3px solid #e5e7eb;
	transition: all 0.2s ease;
}

.collapsible-article :deep(.collapsible-heading:hover) {
	background: transparent;
	border-left-color: #3b82f6;
}

.collapsible-article :deep(.collapsible-heading.collapsed) {
	background: transparent;
	border-left-color: #9ca3af;
	margin-bottom: 4px;
}

.collapsible-article :deep(.collapsible-icon) {
	display: inline-block;
	width: 16px;
	height: 16px;
	font-size: 9px;
	color: #9ca3af;
	background: transparent;
	border-radius: 0;
	transition: transform 0.2s ease, color 0.2s ease;
	margin-right: 8px;
	position: absolute;
	left: 4px;
	top: 50%;
	transform: translateY(-50%);
}

.collapsible-article :deep(.collapsible-heading:hover .collapsible-icon) {
	color: #3b82f6;
}

.collapsible-article :deep(.collapsible-heading h1),
.collapsible-article :deep(.collapsible-heading h2),
.collapsible-article :deep(.collapsible-heading h3) {
	margin: 0;
	padding: 0;
	display: block;
}

.collapsible-article :deep(h1.collapsible-heading),
.collapsible-article :deep(h2.collapsible-heading),
.collapsible-article :deep(h3.collapsible-heading) {
	font-weight: inherit;
}

.collapsible-article :deep(.collapsible-content) {
	overflow: visible;
	max-height: none;
	opacity: 1;
	padding-left: 0;
	border-left: 0;
	margin-left: 0;
	margin-bottom: 16px;
	transition: max-height 0.3s ease, opacity 0.2s ease, padding 0.2s ease, margin 0.2s ease;
}

.collapsible-article :deep(.collapsible-content.collapsed) {
	max-height: 0;
	opacity: 0;
	padding: 0;
	margin-bottom: 0;
}

.collapsible-article :deep(p) {
	margin: 0;
}

.collapsible-article :deep(ul),
.collapsible-article :deep(ol) {
	margin: 0;
	padding-left: 0;
}

.collapsible-article :deep(li) {
	margin: 0;
}

.collapsible-article :deep(img) {
	max-width: 100%;
	height: auto;
	display: inline-block;
	vertical-align: middle;
}

.collapsible-article :deep(video) {
	display: block;
	max-width: 100%;
	height: auto;
}

.collapsible-article :deep(iframe) {
	display: block;
	width: 100%;
	max-width: 100%;
}

.collapsible-article :deep(figure) {
	max-width: 100%;
}

.collapsible-article :deep(.rte-video-card) {
	margin: 28px 0;
	padding: 20px;
	border-radius: 24px;
	background:
		radial-gradient(circle at top right, rgba(56, 189, 248, 0.12), transparent 28%),
		linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
	box-shadow: 0 22px 44px rgba(15, 23, 42, 0.16);
	overflow: hidden;
}

.collapsible-article :deep(.rte-video-card__header) {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 16px;
}

.collapsible-article :deep(.rte-video-card__header-main) {
	min-width: 0;
	flex: 1 1 auto;
}

.collapsible-article :deep(.rte-video-card__eyebrow) {
	margin-bottom: 6px;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: #93c5fd;
}

.collapsible-article :deep(.rte-video-card__title) {
	font-size: 24px;
	line-height: 1.2;
	font-weight: 700;
	color: #f8fafc;
}

.collapsible-article :deep(.rte-video-card__embed) {
	position: relative;
	overflow: hidden;
	border-radius: 20px;
	background: #020617;
	aspect-ratio: 16 / 9;
	box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
}

.collapsible-article :deep(.rte-video-card__iframe),
.collapsible-article :deep(.rte-video-card iframe) {
	display: block;
	width: 100%;
	height: 100%;
	border: 0;
	background: #020617;
}

.collapsible-article :deep(.rte-video-card__embed .vp-custom-player-wrapper) {
	width: 100%;
	height: 100%;
	border-radius: 0;
}

.collapsible-article :deep(.rte-video-card__embed .vp-custom-video-element) {
	width: 100%;
	height: 100%;
}

.collapsible-article :deep(.rte-video-card__fullscreen-btn) {
	border: none;
	border-radius: 999px;
	padding: 10px 14px;
	background: rgba(15, 23, 42, 0.82);
	color: #f8fafc;
	cursor: pointer;
	font-size: 13px;
	font-weight: 600;
	line-height: 1;
	backdrop-filter: blur(10px);
	flex: 0 0 auto;
}

.collapsible-article :deep(.rte-video-card__fullscreen-btn:hover) {
	background: rgba(15, 23, 42, 0.94);
}

.collapsible-article :deep(table) {
	width: 100%;
	border-collapse: collapse;
	margin: 16px 0;
}

.collapsible-article :deep(td),
.collapsible-article :deep(th) {
	padding: 10px 12px;
	border: 1px solid #e2e8f0;
	text-align: left;
}

.collapsible-article :deep(th) {
	background-color: #f8fafc;
	font-weight: 600;
}

.collapsible-article :deep(a) {
	color: #3b82f6;
	text-decoration: none;
}

.collapsible-article :deep(a:hover) {
	text-decoration: underline;
}

.collapsible-article :deep(blockquote) {
	border-left: 4px solid #3b82f6;
	margin: 16px 0;
	padding: 12px 20px;
	background-color: #f8fafc;
	border-radius: 0 8px 8px 0;
	font-style: italic;
	color: #475569;
}

.collapsible-article :deep(code) {
	background-color: #f1f5f9;
	padding: 2px 6px;
	border-radius: 4px;
	font-family: 'Courier New', monospace;
	font-size: 0.9em;
}

.collapsible-article :deep(pre) {
	background-color: #1e293b;
	color: #e2e8f0;
	padding: 16px;
	border-radius: 8px;
	overflow-x: auto;
	margin: 16px 0;
}

.collapsible-article :deep(pre code) {
	background: none;
	padding: 0;
	color: inherit;
}
</style>
